import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useToggle, useUnmount } from 'react-use'
import {
  useGetCourseThemeByIdQuery,
  useGetCourseThemesQuery,
} from '@services/courseThemes'
import { Routes } from '@enums/Routes'
import { ProgressStatuses } from '@enums/ProgressStatuses'
import { useGetCourseByIdQuery } from '@services/courses'
import { useGetCourseChaptersQuery } from '@services/courseChapters'
import { useGetEmployeeEducationDestinationQuery } from '@services/employeeEducationDestination'
import { FinalTestQuestion } from '@models/statistic'
import { FinalTestQuestionInput } from '@models/courseTheme'
import { Scorm } from '@models/scorm'
import Onboarding from '@templates/Onboarding'
import OnboardingCodes from '@enums/OnboardingCodes'
import useMe from '@hooks/useMe'
import Modal from '@organisms/Modal'
import ConfirmModalContent from '@molecules/ConfirmModalContent'
import PrimaryButton from '@organisms/PrimaryButton'
import passBear from '@interface-images/ellipse-41.png'
import {
  usePostEndMutation,
  usePostStartMutation,
  usePostTestResultsMutation,
} from '@services/statistic'
import {
  createStatsCommonForm,
  toEducationDestinationIri,
} from '@utils/courseThemeStats'
import ControlQuestionModal from '@modals/ControlQuestionModal'
import ScormViewer from './ScormViewer'
import PdfViewer from './PdfViewer'
import FinalTestViewer from './FinalTestViewer'
import { ScormWrapper } from './styled'
import { Container } from './ScormViewer/styled'
import useScormEventSender from './useScormEventSender'
import usePdfEventSender from './usePdfEventSender'
import { useCourseCompletionModal } from './useCourseCompletionModal'
import { ONBOARDING_STEPS, ONBOARDING_TITLE } from './onboarding'

type ClientCourseTheme = Scorm & {
  courseTheme?: Scorm
}

type ThemeCourse = {
  isClientCourse?: boolean
  ownerCompany?: unknown
  ownerBranch?: unknown
  ownerSubdivision?: unknown
}

type ThemeCourseSource = {
  course?: ThemeCourse
  courseChapter?: {
    course?: ThemeCourse
  }
}

const isThemeClientCourse = (source: ThemeCourseSource | undefined) => {
  const course = source?.courseChapter?.course ?? source?.course

  return (
    course?.isClientCourse === true ||
    !!course?.ownerCompany ||
    !!course?.ownerBranch ||
    !!course?.ownerSubdivision
  )
}

const isFinalTestScorePassed = (
  passedPercent: number,
  passingScore: number
) => passedPercent > 0 && passedPercent >= passingScore

// eslint-disable-next-line sonarjs/cognitive-complexity
const Theme: FC = () => {
  const { themeId = '', courseId = '', educationId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation() as {
    pathname: string
    state?: {
      openControlQuestion?: boolean
      isPreviewMode?: boolean
      returnTo?: string
    }
  }

  const [inModal, toggleInModal] = useToggle(false)
  const [controlQuestionVisible, setControlQuestionVisible] = useState(false)
  const [controlQuestionStatus, setControlQuestionStatus] = useState<
    'idle' | 'wrong' | 'success'
  >('idle')
  const [wrongControlQuestionIndex, setWrongControlQuestionIndex] = useState<
    number | null
  >(null)
  const [controlQuestionAttempts, setControlQuestionAttempts] = useState(0)
  const [isSubmittingControlAnswer, setIsSubmittingControlAnswer] = useState(false)
  const [isControlQuestionPassed, setIsControlQuestionPassed] = useState(false)
  const [finalTestQuestionIndex, setFinalTestQuestionIndex] = useState(0)
  const [isSubmittingFinalTestAnswer, setIsSubmittingFinalTestAnswer] =
    useState(false)
  const [finalTestQuestionOrder, setFinalTestQuestionOrder] = useState<
    number[] | null
  >(null)
  const [finalTestQuestionResults, setFinalTestQuestionResults] = useState<
    Record<number, boolean>
  >({})
  const [finalTestStartedAt, setFinalTestStartedAt] = useState<number | null>(
    null
  )
  const [finalTestEducationDestination, setFinalTestEducationDestination] =
    useState<string | undefined>(undefined)
  // Ключ последнего старта итогового теста (тема+обучение). Не даём эффекту
  // стартовать тест повторно и плодить пустую попытку, если он переотработает
  // из-за фонового ре-рендера (например, опрос /users/me).
  const finalTestStartKeyRef = useRef<string | null>(null)
  // Отложенный переход: куда пользователь хотел уйти, когда контрольный вопрос
  // заблокировал навигацию. Выполняется после закрытия модалки успеха.
  const pendingNavigationRef = useRef<(() => void) | null>(null)
  const [controlQuestionEducationDestination, setControlQuestionEducationDestination] =
    useState<string | undefined>(undefined)
  const { isAdmin } = useMe()
  const isPreviewMode = Boolean(location.state?.isPreviewMode)
  const shouldCollectStats = !isPreviewMode

  const { data: courseData } = useGetCourseByIdQuery(courseId, {
    skip: !courseId,
  })
  const [postEnd] = usePostEndMutation()
  const [postStart] = usePostStartMutation()
  const [postTestResults] = usePostTestResultsMutation()
  const initialEducationDestination = toEducationDestinationIri(educationId)
  const courseThemeQuery = initialEducationDestination
    ? { id: themeId, forEmployeeEducation: initialEducationDestination }
    : themeId
  const { data: courseThemeData, refetch: refetchCourseTheme } =
    useGetCourseThemeByIdQuery(courseThemeQuery)
  const {
    title,
    nextThemeId,
    prevThemeId,
    courseTheme,
    test,
    currentListenerStatus,
    controlQuestion,
    source,
    questions: courseThemeQuestions,
    questionsCount,
  } = courseThemeData || {}
  const finalTestEducationDestinationIri =
    finalTestEducationDestination || initialEducationDestination

  // Карточка 133: прогресс курса (сколько тем пройдено из всех) для плашки
  // «Вы успешно прошли курс» на странице темы. Для назначенных курсов id назначения
  // есть в адресе (educationId). Бесплатные (educationId=0) сюда не попадают —
  // это отдельный шаг.
  const courseDestinationId = Number(educationId)
  const hasCourseDestination =
    Number.isFinite(courseDestinationId) && courseDestinationId > 0
  const {
    data: courseDestinationData,
    isFetching: isCourseDestinationFetching,
    refetch: refetchCourseDestination,
  } = useGetEmployeeEducationDestinationQuery(courseDestinationId, {
    skip: !hasCourseDestination || !shouldCollectStats,
    refetchOnMountOrArgChange: true,
  })
  // Курс пройден целиком = статус назначения PASSED (учитывает ВСЕ темы, включая
  // итоговый тест). Число пройденных тем итоговый не учитывает — по нему нельзя.
  const isCoursePassed =
    courseDestinationData === undefined
      ? undefined
      : courseDestinationData.currentStatus === ProgressStatuses.PASSED
  const { isCourseCompletionVisible, closeCourseCompletion } =
    useCourseCompletionModal(isCoursePassed, isCourseDestinationFetching)

  // Клиентский путь: после ответа на контрольный вопрос не показываем «прошли тему»
  // сразу. Ждём свежий статус курса (считается на бэке, приходит после перезапроса),
  // потом решаем в эффекте ниже — плашка курса или «прошли тему». Без мигания.
  const awaitingCourseCompletionRef = useRef(false)
  const courseStatusRefetchSeenRef = useRef(false)

  useEffect(() => {
    if (!awaitingCourseCompletionRef.current) {
      return
    }
    if (isCourseDestinationFetching) {
      courseStatusRefetchSeenRef.current = true
      return
    }
    // Ждём, пока перезапрос назначения реально прошёл (isFetching true -> false).
    if (!courseStatusRefetchSeenRef.current) {
      return
    }

    awaitingCourseCompletionRef.current = false
    courseStatusRefetchSeenRef.current = false
    setIsSubmittingControlAnswer(false)

    if (courseDestinationData?.currentStatus === ProgressStatuses.PASSED) {
      // Курс завершён целиком — плашку курса покажет useCourseCompletionModal,
      // модалку контрольного вопроса просто закрываем (без «прошли тему»).
      setControlQuestionVisible(false)
    } else {
      setControlQuestionStatus('success')
    }
  }, [isCourseDestinationFetching, courseDestinationData])

  const isManualOrXls = source === 'manual' || source === 'xls'

  const finalTestQuestions = useMemo<FinalTestQuestion[]>(() => {
    if (!isManualOrXls || !courseThemeQuestions?.length) {
      return []
    }

    if (!isAdmin && !isPreviewMode && finalTestQuestionOrder === null) {
      return []
    }

    const shuffledAnswers = (answers: FinalTestQuestionInput['answers']) =>
      answers
        .map((answer, index) => ({
          id: `answer-${index}`,
          text: answer.text,
          isCorrect: answer.isCorrect,
        }))
        .sort(() => Math.random() - 0.5)

    const questionByIndex = courseThemeQuestions.map((question, index) => ({
      text: question.question,
      type: question.type,
      index,
      questionId: index,
      answers: question.answers,
    }))

    const getQuestion = (questionIndex: number): FinalTestQuestion | null => {
      const question = questionByIndex[questionIndex]

      if (!question) {
        return null
      }

      return {
        text: question.text,
        type: question.type,
        questionId: question.questionId,
        options: shuffledAnswers(question.answers),
      }
    }

    const persistedQuestionOrder = finalTestQuestionOrder || []
    const usedIndexes = new Set<number>()
    const questionIndexes = persistedQuestionOrder.length > 0
      ? persistedQuestionOrder.reduce((acc, questionIndex) => {
          const normalizedIndex = Number(questionIndex)
          if (
            !Number.isInteger(normalizedIndex) ||
            usedIndexes.has(normalizedIndex)
          ) {
            return acc
          }

          if (!questionByIndex[normalizedIndex]) {
            return acc
          }

          usedIndexes.add(normalizedIndex)
          acc.push(normalizedIndex)

          return acc
        }, [] as number[])
      : questionByIndex.map(({ index }) => index)

    if (persistedQuestionOrder.length > 0) {
      questionByIndex.forEach(({ index }) => {
        if (!usedIndexes.has(index)) {
          usedIndexes.add(index)
          questionIndexes.push(index)
        }
      })
    }

    const selectedQuestions = questionIndexes
      .map((questionIndex) => getQuestion(questionIndex))
      .filter((question): question is FinalTestQuestion => Boolean(question))

    if (selectedQuestions.length === 0) {
      return []
    }

    if (persistedQuestionOrder.length === 0 && !isPreviewMode && !isAdmin) {
      selectedQuestions.sort(() => Math.random() - 0.5)
    }

    const totalQuestions =
      isPreviewMode
        ? selectedQuestions.length
        : typeof questionsCount === 'number' && questionsCount > 0
        ? Math.min(questionsCount, selectedQuestions.length)
        : selectedQuestions.length

    return selectedQuestions.slice(0, totalQuestions)
  }, [
    courseThemeQuestions,
    isAdmin,
    isManualOrXls,
    isPreviewMode,
    finalTestQuestionOrder,
    questionsCount,
  ])

  useEffect(() => {
    if (!isManualOrXls || isAdmin || isPreviewMode || !themeId) {
      finalTestStartKeyRef.current = null
      setFinalTestQuestionIndex(0)
      setFinalTestQuestionOrder([])
      setFinalTestEducationDestination(undefined)
      setFinalTestQuestionResults({})
      setFinalTestStartedAt(null)

      return
    }

    // Стартуем тест ровно один раз на связку тема+обучение. Повторный прогон
    // эффекта с тем же ключом пропускаем — иначе создаётся вторая пустая попытка,
    // которая затем оседает в ведомости строкой «0 сек / 0% (0 из 1)».
    const startKey = `${themeId}:${initialEducationDestination ?? ''}`
    if (finalTestStartKeyRef.current === startKey) {
      return
    }
    finalTestStartKeyRef.current = startKey

    setFinalTestQuestionIndex(0)
    setFinalTestQuestionOrder(null)
    setFinalTestEducationDestination(undefined)
    setFinalTestQuestionResults({})
    setFinalTestStartedAt(null)

    const initFinalTest = async () => {
      try {
        const startResponse = await postStart(
          createStatsCommonForm(themeId, initialEducationDestination)
        ).unwrap()

        setFinalTestEducationDestination(
          startResponse.educationDestination || initialEducationDestination
        )
        setFinalTestStartedAt(Date.now())
        setFinalTestQuestionOrder(
          (startResponse.questionsOrder || [])
            .map((questionId) => Number(questionId))
            .filter((questionId) => Number.isInteger(questionId))
        )
      } catch {
        // ignore start errors for manual/xls final tests
      }
    }

    void initFinalTest()
  }, [
    isManualOrXls,
    isAdmin,
    isPreviewMode,
    themeId,
    initialEducationDestination,
    postStart,
  ])

  useEffect(() => {
    setIsControlQuestionPassed(false)
    setControlQuestionStatus('idle')
    setWrongControlQuestionIndex(null)
    setControlQuestionAttempts(0)
    setIsSubmittingControlAnswer(false)
    setControlQuestionEducationDestination(initialEducationDestination)
  }, [initialEducationDestination, themeId])

  const resolveControlQuestionEducationDestination = useCallback(async () => {
    if (isPreviewMode) {
      return controlQuestionEducationDestination
    }

    if (!themeId) {
      return controlQuestionEducationDestination
    }

    try {
      const startResponse = await postStart(
        createStatsCommonForm(themeId, controlQuestionEducationDestination)
      ).unwrap()

      const destination =
        startResponse.educationDestination || controlQuestionEducationDestination

      setControlQuestionEducationDestination(destination)

      return destination
    } catch {
      return controlQuestionEducationDestination
    }
  }, [
    controlQuestionEducationDestination,
    isPreviewMode,
    themeId,
    postStart,
  ])

  useEffect(() => {
    if (String(courseThemeData?.id) !== themeId) {
      return
    }

    setIsControlQuestionPassed(
      (wasPassed) =>
        wasPassed || currentListenerStatus === ProgressStatuses.PASSED
    )
  }, [courseThemeData?.id, currentListenerStatus, themeId])

  const finalTestThemeId = courseData?.finalTest?.id
  type ThemeContent = NonNullable<typeof courseTheme>
  const courseThemeWithNested = courseTheme as
    | (ThemeContent & { courseTheme?: ThemeContent })
    | undefined
  const contentTheme = courseThemeWithNested?.courseTheme ?? courseThemeWithNested
  const isClientCourse =
    isThemeClientCourse(courseThemeWithNested as ThemeCourseSource) ||
    isThemeClientCourse(contentTheme as ThemeCourseSource) ||
    !!courseData?.isClientCourse
  // У клиентских курсов итоговое — отдельная сущность course.finalTest. У общих её
  // нет: там итоговое задаётся обычной SCORM-темой с галочкой «Содержит тест».
  // Поэтому для общих курсов итоговым считаем ПОСЛЕДНЮЮ такую тему курса: помечена
  // тестом и следующей темы за ней уже нет (навигация prev/next сквозная по всему
  // курсу, а не внутри раздела). Промежуточные темы с тестом под условие не попадают.
  const isFinalTest =
    courseData?.finalTest?.id === Number(themeId) ||
    (!isClientCourse && test === true && !nextThemeId)
  const clientCourseTheme = (courseTheme as ClientCourseTheme | undefined)
    ?.courseTheme
  const contentThemeId = String(
    isClientCourse
      ? (clientCourseTheme?.id || contentTheme?.id || '')
      : (contentTheme?.id || '')
  )
  const contentThemeIri = isClientCourse
    ? clientCourseTheme?.['@id']
    : contentTheme?.['@id']
  const themeScormIri = contentThemeIri ?? courseTheme?.['@id']

  const isTestBlocked =
    !!controlQuestion && !isControlQuestionPassed

  const controlQuestionPassed = isControlQuestionPassed || currentListenerStatus === ProgressStatuses.PASSED

  const showControlQuestionButton =
    isClientCourse &&
    !!controlQuestion &&
    !controlQuestionPassed &&
    !isFinalTest

  const isPdf = contentTheme?.type === 'pdf'
  const hasControlQuestion = courseThemeData ? !!controlQuestion : undefined

  const openControlQuestionModal = useCallback(() => {
    // Открытие без навигационного намерения (явная кнопка «Ответить на контрольный
    // вопрос», редирект): сбрасываем отложенный переход, чтобы не сработал чужой.
    pendingNavigationRef.current = null
    setControlQuestionVisible(true)
    setControlQuestionStatus('idle')
    setWrongControlQuestionIndex(null)
  }, [])

  const closeControlQuestionModal = () => {
    setControlQuestionVisible(false)
    setControlQuestionStatus('idle')
    setWrongControlQuestionIndex(null)
  }

  // Куда пользователь хотел уйти, когда контрольный вопрос заблокировал навигацию.
  // Выполняем этот переход после закрытия модалки успеха.
  const handleControlQuestionModalClose = () => {
    const passed =
      controlQuestionStatus === 'success' || isControlQuestionPassed
    const pendingNavigation = pendingNavigationRef.current
    pendingNavigationRef.current = null

    closeControlQuestionModal()

    if (passed && pendingNavigation) {
      pendingNavigation()
    }
  }

  const handleBlockedNavigation = (pendingNavigation?: () => void) => {
    openControlQuestionModal()
    pendingNavigationRef.current = pendingNavigation ?? null
  }

  const handleBack = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo)

      return
    }

    if (isPreviewMode) {
      navigate(-1)

      return
    }

    navigate(
      courseId
        ? Routes.COURSES + `/${courseId}/education/${educationId}`
        : Routes.COURSES
    )
  }

  const guardNavigation = (action: () => void) => {
    if (isTestBlocked) {
      openControlQuestionModal()
      pendingNavigationRef.current = action
    } else {
      action()
    }
  }

  const guardedHandleBack = () => guardNavigation(handleBack)

  // У SCORM-итогового нет своей кнопки "продолжить" — при прохождении
  // показываем сообщение о прохождении курса так же, как ручной/xls путь.
  const handleScormFinalTestPassed = (scormEducationDestinationIri?: string) => {
    // У бесплатных курсов в адресе education/0, поэтому назначения из URL нет.
    // Бэкенд создаёт его сам при старте SCORM — берём то, что вернул хук.
    const educationDestinationIri =
      scormEducationDestinationIri || finalTestEducationDestinationIri

    if (!isFinalTest || !educationDestinationIri) {
      return
    }

    navigate(Routes.STATISTIC, {
      state: {
        finalTestResult: {
          educationDestinationIri,
          courseTitle: courseData?.title || '',
          courseId,
          educationId,
          themeId,
          testSuccess: true,
          showCourseCompletionModal: true,
        },
      },
    })
  }

  // Use appropriate event sender based on theme type
  const handleScormChangePage = useScormEventSender(
    courseData?.type,
    courseTheme?.scormType,
    test,
    courseData?.free,
    hasControlQuestion,
    isControlQuestionPassed,
    isPreviewMode,
    handleScormFinalTestPassed
  )
  const handlePdfChangePage = usePdfEventSender(
    courseData?.free,
    hasControlQuestion,
    isControlQuestionPassed,
    isPreviewMode,
    isPdf
  )

  const handleChangePage = isPdf ? handlePdfChangePage : handleScormChangePage

  useUnmount(handleChangePage)

  const { data: finalTestCourseChapters } = useGetCourseChaptersQuery(
    { course: courseId, pagination: false },
    { skip: !courseId || !isFinalTest }
  )
  const finalTestFallbackChapterId = useMemo(() => {
    const courseChapters = finalTestCourseChapters?.items || []

    return [...courseChapters].sort((a, b) => {
      if (a.sortOrder === b.sortOrder) {
        return a.id - b.id
      }

      return a.sortOrder - b.sortOrder
    }).at(-1)?.id
  }, [finalTestCourseChapters?.items])
  const { data: finalTestFallbackThemes } = useGetCourseThemesQuery(
    { courseChapter: finalTestFallbackChapterId, pagination: false },
    { skip: !isFinalTest || !finalTestFallbackChapterId }
  )
  const finalTestFallbackPrevThemeId = useMemo(() => {
    const courseThemes = (finalTestFallbackThemes?.items || []).filter(
      ({ id }) => String(id) !== themeId
    )

    return [...courseThemes].sort((a, b) => {
      if (a.sortOrder === b.sortOrder) {
        return a.id - b.id
      }

      return (a.sortOrder || 0) - (b.sortOrder || 0)
    }).at(-1)?.id
  }, [finalTestFallbackThemes?.items, themeId])
  const fallbackPrevThemeId = isFinalTest
    ? finalTestFallbackPrevThemeId
    : undefined
  const resolvedPrevThemeId = prevThemeId || fallbackPrevThemeId
  const finalTestRoute = finalTestThemeId &&
    !isFinalTest &&
    String(finalTestThemeId) !== String(themeId)
      ? `/themes/${finalTestThemeId}/course/${courseId}/education/${educationId}`
      : undefined

  const navigationProps = {
    nextRoute: nextThemeId
      ? `/themes/${nextThemeId}/course/${courseId}/education/${educationId}`
      : finalTestRoute,
    prevRoute: resolvedPrevThemeId
      ? `/themes/${resolvedPrevThemeId}/course/${courseId}/education/${educationId}`
      : undefined,
    onChangePage: handleChangePage,
    isTestBlocked,
    onBlockedNavigation: handleBlockedNavigation,
  }

  // Build PDF file path from the file object
  const pdfFilePath = contentTheme?.file?.filePath
    ? `/uploads/${contentTheme.file.filePath}`
    : ''

  useEffect(() => {
    if (
      !location.state?.openControlQuestion ||
      !controlQuestion ||
      isControlQuestionPassed
    ) {
      return
    }

    openControlQuestionModal()
    navigate(location.pathname, { replace: true })
  }, [
    controlQuestion,
    isControlQuestionPassed,
    location.pathname,
    location.state?.openControlQuestion,
    openControlQuestionModal,
    navigate,
  ])

  const handleControlQuestionAnswer = async (selectedIndex: number) => {
    if (!controlQuestion || !themeId || isSubmittingControlAnswer) {
      return
    }
    if (isControlQuestionPassed || controlQuestionStatus === 'success') {
      return
    }

    setIsSubmittingControlAnswer(true)

    const isSuccess = !!controlQuestion.options[selectedIndex]?.isCorrect
    const nextAttempt = controlQuestionAttempts + 1

    if (!shouldCollectStats) {
      setControlQuestionAttempts(nextAttempt)

      if (!isSuccess) {
        setControlQuestionStatus('wrong')
        setWrongControlQuestionIndex(selectedIndex)
        setIsSubmittingControlAnswer(false)
        return
      }

      setIsControlQuestionPassed(true)
      setControlQuestionStatus('success')
      setWrongControlQuestionIndex(null)
      setIsSubmittingControlAnswer(false)
      return
    }

    const educationDestination = await resolveControlQuestionEducationDestination()
    const commonForm = createStatsCommonForm(
      themeId,
      educationDestination
    )
    try {
      await postTestResults({
        ...commonForm,
        questionNumber: nextAttempt,
        questionCount: 1,
        success: isSuccess,
      }).unwrap()
    } catch {
      setIsSubmittingControlAnswer(false)
      return
    }

    setControlQuestionAttempts(nextAttempt)

    if (!isSuccess) {
      setControlQuestionStatus('wrong')
      setWrongControlQuestionIndex(selectedIndex)
      setIsSubmittingControlAnswer(false)
      return
    }

    try {
      await postEnd({ ...commonForm, testSuccess: true }).unwrap()
    } catch {
      setIsSubmittingControlAnswer(false)
      return
    }

    setIsControlQuestionPassed(true)
    setWrongControlQuestionIndex(null)
    void refetchCourseTheme()

    // Карточка 133: не показываем «прошли тему» сразу. Ждём свежий статус курса
    // (перезапрос назначения), решение принимает эффект выше: курс пройден целиком ->
    // плашка курса, иначе -> «прошли тему». Кнопка остаётся заблокированной до решения.
    if (!hasCourseDestination) {
      setControlQuestionStatus('success')
      setIsSubmittingControlAnswer(false)
      return
    }

    awaitingCourseCompletionRef.current = true
    courseStatusRefetchSeenRef.current = false
    void refetchCourseDestination()
  }

  const handleControlQuestionSelectionChange = () => {
    if (controlQuestionStatus === 'wrong') {
      setControlQuestionStatus('idle')
      setWrongControlQuestionIndex(null)
    }
  }

  const handleFinalTestAnswer = async (
    _selectedOptionIds: (string | number)[],
    isSuccess: boolean
  ) => {
    const currentQuestion = finalTestQuestions[finalTestQuestionIndex]

    if (
      !themeId ||
      isSubmittingFinalTestAnswer ||
      !currentQuestion
    ) {
      return
    }

    setIsSubmittingFinalTestAnswer(true)
    const questionId = currentQuestion.questionId

    if (!shouldCollectStats) {
      setFinalTestQuestionResults((prev) => ({
        ...prev,
        [questionId]: isSuccess,
      }))
      setIsSubmittingFinalTestAnswer(false)
      return
    }

    if (!finalTestEducationDestinationIri) {
      setIsSubmittingFinalTestAnswer(false)
      return
    }

    const commonForm = createStatsCommonForm(
      themeId,
      finalTestEducationDestinationIri
    )

    try {
      await postTestResults({
        ...commonForm,
        questionNumber: finalTestQuestionIndex + 1,
        questionCount: finalTestQuestions.length,
        questionId,
        selectedAnswers: _selectedOptionIds,
        success: isSuccess,
      }).unwrap()
    } catch (error) {
      setIsSubmittingFinalTestAnswer(false)
      throw error
    }

    setFinalTestQuestionResults((prev) => ({
      ...prev,
      [questionId]: isSuccess,
    }))
    setIsSubmittingFinalTestAnswer(false)
  }

  const handleFinalTestContinue = async () => {
    if (
      isSubmittingFinalTestAnswer ||
      !themeId
    ) {
      return
    }

    const nextQuestion = finalTestQuestionIndex + 1

    const commonForm = createStatsCommonForm(
      themeId,
      finalTestEducationDestinationIri
    )

    setIsSubmittingFinalTestAnswer(true)

    if (nextQuestion >= finalTestQuestions.length) {
      if (!shouldCollectStats) {
        handleBack()
        setIsSubmittingFinalTestAnswer(false)

        return
      }

      if (!finalTestEducationDestinationIri) {
        setIsSubmittingFinalTestAnswer(false)
        return
      }

      const finalTestQuestionCount = finalTestQuestions.length
      const finalTestPassedCount = finalTestQuestions.reduce((count, question) => {
        const questionResult = finalTestQuestionResults[question.questionId]

        return count + (questionResult ? 1 : 0)
      }, 0)
      const finalTestPassingScore = Number(courseThemeData?.passPercentage ?? 100)
      const finalTestPassedPercent = finalTestQuestionCount
        ? Math.round((finalTestPassedCount / finalTestQuestionCount) * 100)
        : 0
      const isFinalTestPassed = isFinalTestScorePassed(
        finalTestPassedPercent,
        finalTestPassingScore
      )
      const secondsDuration = finalTestStartedAt
        ? Math.max(0, Math.round((Date.now() - finalTestStartedAt) / 1000))
        : undefined

      let endResponse
      try {
        endResponse = await postEnd({
          ...commonForm,
          testSuccess: isFinalTestPassed,
        }).unwrap()
      } catch {
        setIsSubmittingFinalTestAnswer(false)
        return
      }

      navigate(Routes.STATISTIC, {
        state: {
          finalTestResult: {
            educationDestinationIri: finalTestEducationDestinationIri,
            courseTitle: courseData?.title || title || '',
            courseId,
            educationId,
            themeId,
            passPercentage: courseThemeData?.passPercentage,
            questionsCount: finalTestQuestionCount,
            questionsPassed: finalTestPassedCount,
            secondsDuration,
            testSuccess: isFinalTestPassed,
            passingScore: endResponse.passingScore,
            showCourseCompletionModal: true,
          },
        },
      })
      setIsSubmittingFinalTestAnswer(false)

      return
    }

    setFinalTestQuestionIndex(nextQuestion)
    setIsSubmittingFinalTestAnswer(false)
  }

  return (
    <>
      <Onboarding
        firstStepTitle={ONBOARDING_TITLE}
        steps={ONBOARDING_STEPS}
        code={OnboardingCodes.my_education_theme}
        withScroll
      />
      {isManualOrXls ? (
        <FinalTestViewer
          {...navigationProps}
          questions={finalTestQuestions}
          questionIndex={finalTestQuestionIndex}
          onAnswer={handleFinalTestAnswer}
          onContinue={handleFinalTestContinue}
          handleBack={handleBack}
          inModal={inModal}
          toggleInModal={toggleInModal}
          isPreviewMode={isPreviewMode}
        />
      ) : (
        <Container id="themeContainer">
          <ScormWrapper inModal={inModal}>
            {isPdf ? (
              <PdfViewer
                {...navigationProps}
                filePath={pdfFilePath}
                currentVote={contentTheme?.currentVote}
                scormId={contentThemeId}
                scormIri={themeScormIri}
                inModal={inModal}
                toggleInModal={toggleInModal}
                handleBack={guardedHandleBack}
                isClientCourse={isClientCourse}
                themeId={contentThemeId}
                showControlQuestionButton={showControlQuestionButton}
                onControlQuestion={openControlQuestionModal}
                isPreviewMode={isPreviewMode}
              />
            ) : (
              <ScormViewer
                {...navigationProps}
                title={title}
                currentVote={contentTheme?.currentVote}
                scormId={contentThemeId}
                scormIri={themeScormIri}
                scormDirectory={contentTheme?.scormDirectory || ''}
                inModal={inModal}
                toggleInModal={toggleInModal}
                scormType={contentTheme?.scormType || 'articulate'}
                handleBack={guardedHandleBack}
                isClientCourse={isClientCourse}
                themeId={contentThemeId}
                showControlQuestionButton={showControlQuestionButton}
                onControlQuestion={openControlQuestionModal}
                isPreviewMode={isPreviewMode}
              />
            )}
          </ScormWrapper>
        </Container>
      )}
      {controlQuestion && (
        <Modal
          visible={controlQuestionVisible}
          onModalClose={handleControlQuestionModalClose}
        >
          <ControlQuestionModal
            controlQuestion={controlQuestion}
            isCorrectlyAnswered={controlQuestionStatus === 'success'}
            wrongSelectedIndex={wrongControlQuestionIndex}
            onAnswer={handleControlQuestionAnswer}
            onClose={handleControlQuestionModalClose}
            isSubmitting={isSubmittingControlAnswer}
            onSelectionChange={handleControlQuestionSelectionChange}
          />
        </Modal>
      )}
      {isCourseCompletionVisible && (
        <Modal visible onModalClose={closeCourseCompletion}>
          <ConfirmModalContent
            iconSrc={passBear}
            title="Вы успешно прошли курс"
            rightContent={
              <PrimaryButton onClick={closeCourseCompletion} big autoWidth>
                Хорошо
              </PrimaryButton>
            }
          />
        </Modal>
      )}
    </>
  )
}

export default Theme
